// src/app/patients/components/PatientIntakeWizard.jsx

import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Grid,
  TextField,
  MenuItem,
  Alert,
  Button,
  Stack,
  Divider,
} from "@mui/material";

import { createPatient } from "../api/patientsApi";
import { createAdmission } from "../api/admissionsApi";
import { prettyError } from "../utils/errors";

import {
  listHospitals,
  listDepartmentsByHospital,
  listWardsByHospital,
} from "../api/coreLookupApi";

const STEPS = ["Patient", "Emergency", "Admission", "Review"];

const SEX_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "OTHER", label: "Other" },
  { value: "UNKNOWN", label: "Unknown" },
];

const twoCol = { xs: 12, sm: 6 };

function toIsoFromDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  return `${dateStr}T${timeStr}:00+05:30`;
}

/** ---------- Pickers (support many backend key names) ---------- */
const pickHospitalId = (h) => h?.hospital_id || h?.id || h?.uuid || h?.pk;
const pickHospitalName = (h) => h?.name || h?.hospital_name || h?.title || "Hospital";

const pickDeptId = (d) => d?.department_id || d?.id || d?.uuid || d?.pk;
const pickDeptName = (d) => d?.name || d?.department_name || d?.title || "Department";

const pickWardId = (w) => w?.ward_id || w?.id || w?.uuid || w?.pk;
const pickWardName = (w) => w?.name || w?.ward_name || w?.unit_name || "Ward";

export default function PatientIntakeWizard() {
  const [activeStep, setActiveStep] = React.useState(0);

  const [form, setForm] = React.useState({
    // Patient
    full_name: "",
    age_years: "",
    sex: "",
    uhid: "",
    primary_phone: "",
    address: "",

    // Emergency
    emergency_contact_name: "",
    emergency_relationship: "",
    emergency_contact_number: "",

    // Admission
    organization: "", // hospital UUID / id
    department: "", // store NAME
    ward_unit_id: "", // store ward ID
    ward_unit: "", // store ward NAME
    bed_number: "", // ✅ MANUAL
    room_number: "", // ✅ MANUAL

    admission_date: "",
    admission_time: "",
    treating_doctor: "",
    consultant: "",
  });

  const [orgs, setOrgs] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);
  const [wards, setWards] = React.useState([]);

  const [loadingOrg, setLoadingOrg] = React.useState(false);
  const [loadingDept, setLoadingDept] = React.useState(false);
  const [loadingWard, setLoadingWard] = React.useState(false);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  /** ---------- Load hospitals once ---------- */
  React.useEffect(() => {
    let alive = true;
    setLoadingOrg(true);

    listHospitals()
      .then((r) => {
        if (!alive) return;
        const data = r.data?.results ?? r.data ?? [];
        setOrgs(data);
      })
      .catch(() => {})
      .finally(() => alive && setLoadingOrg(false));

    return () => {
      alive = false;
    };
  }, []);

  /** ---------- Organization (Hospital) change => fetch dept+ward ---------- */
  const onOrgChange = async (e) => {
    const hospitalId = e.target.value;

    setError("");
    setSuccess("");

    setForm((p) => ({
      ...p,
      organization: hospitalId,
      department: "",
      ward_unit_id: "",
      ward_unit: "",
      bed_number: "",
      room_number: "",
      admission_date: "",
      admission_time: "",
    }));

    setDepartments([]);
    setWards([]);

    if (!hospitalId) return;

    setLoadingDept(true);
    setLoadingWard(true);

    try {
      const [dRes, wRes] = await Promise.all([
        listDepartmentsByHospital(hospitalId),
        listWardsByHospital(hospitalId),
      ]);

      setDepartments(dRes.data?.results ?? dRes.data ?? []);
      setWards(wRes.data?.results ?? wRes.data ?? []);
    } catch (err) {
    } finally {
      setLoadingDept(false);
      setLoadingWard(false);
    }
  };

  /** ---------- Validation ---------- */
  const validateStep = (step) => {
    const uhid = form.uhid.trim();

    if (step === 0) {
      if (!form.full_name.trim()) return "Full Name is required";
      if (!uhid) return "UHID is required";
      if (!/^\d+$/.test(uhid)) return "UHID must be numbers only";
      if (form.age_years && !/^\d+$/.test(form.age_years)) return "Age must be a number";
      if (form.primary_phone && !/^\+?[0-9]{7,15}$/.test(form.primary_phone))
        return "Mobile must be 7-15 digits (optional +)";
    }

    if (step === 1) {
      if (form.emergency_contact_number && !/^\+?[0-9]{7,15}$/.test(form.emergency_contact_number))
        return "Emergency number must be 7-15 digits (optional +)";
    }

    if (step === 2) {
      if (!form.organization) return "Hospital/Organization is required";
      if (!form.admission_date) return "Date of Admission is required";
      if (!form.admission_time) return "Time of Admission is required";
      // Ward optional? If you want mandatory:
      // if (!form.ward_unit_id) return "Ward/Unit is required";
    }

    return null;
  };

  const next = () => {
    setError("");
    const msg = validateStep(activeStep);
    if (msg) return setError(msg);
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => {
    setError("");
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const resetAll = () => {
    setError("");
    setSuccess("");
    setActiveStep(0);
    setForm({
      full_name: "",
      age_years: "",
      sex: "",
      uhid: "",
      primary_phone: "",
      address: "",
      emergency_contact_name: "",
      emergency_relationship: "",
      emergency_contact_number: "",
      organization: "",
      department: "",
      ward_unit_id: "",
      ward_unit: "",
      bed_number: "",
      room_number: "",
      admission_date: "",
      admission_time: "",
      treating_doctor: "",
      consultant: "",
    });
    setDepartments([]);
    setWards([]);
  };

  /** ---------- Save: Patient + Admission ---------- */
  const saveAll = async () => {
    setError("");
    setSuccess("");

    for (let s = 0; s <= 2; s++) {
      const msg = validateStep(s);
      if (msg) {
        setActiveStep(s);
        return setError(msg);
      }
    }

    const patientPayload = {
      uhid: form.uhid.trim(),
      full_name: form.full_name.trim(),
      age_years: form.age_years ? Number(form.age_years) : null,
      sex: form.sex || null,
      primary_phone: form.primary_phone || null,
      address: form.address || "",
      emergency_contact_name: form.emergency_contact_name || null,
      emergency_relationship: form.emergency_relationship || null,
      emergency_contact_number: form.emergency_contact_number || null,
    };

    const admit_at = toIsoFromDateTime(form.admission_date, form.admission_time);

    const admissionPayload = {
      patient_uhid: form.uhid.trim(),
      organization: form.organization,
      admit_at,
      admit_source: "OTHER",
      status: "ACTIVE",

      department: form.department || null,
      ward_unit: form.ward_unit || null,

      // ✅ now manual
      bed_number: form.bed_number || null,
      room_number: form.room_number || null,

      treating_doctor: form.treating_doctor || null,
      consultant: form.consultant || null,
    };

    try {
      setLoading(true);
      await createPatient(patientPayload);
      const res = await createAdmission(admissionPayload);

      setSuccess(`✅ Saved | UHID: ${patientPayload.uhid} | Admission No (AUTO): ${res.data.admission_no}`);
      setActiveStep(3);
    } catch (err) {
      setError(prettyError(err));
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ children }) => (
    <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>
      {children}
    </Typography>
  );

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 3,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 980, borderRadius: 3, boxShadow: 6 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h5" fontWeight={900}>
                Patient Intake Wizard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hospital → Department/Ward (dropdown) + Bed/Room (manual)
              </Typography>
            </Box>

            <Stepper activeStep={activeStep} alternativeLabel>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <Divider />

            {/* STEP 0 */}
            {activeStep === 0 && (
              <Box>
                <SectionTitle>Patient Details</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item {...twoCol}>
                    <TextField fullWidth label="Full Name *" value={form.full_name} onChange={set("full_name")} />
                  </Grid>
                  <Grid item {...twoCol}>
                    <TextField fullWidth label="Age (years)" value={form.age_years} onChange={set("age_years")} />
                  </Grid>

                  <Grid item {...twoCol}>
                    <TextField select fullWidth label="Gender" value={form.sex} onChange={set("sex")}>
                      <MenuItem value="">Select</MenuItem>
                      {SEX_OPTIONS.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item {...twoCol}>
                    <TextField fullWidth label="UHID (numbers) *" value={form.uhid} onChange={set("uhid")} />
                  </Grid>

                  <Grid item {...twoCol}>
                    <TextField fullWidth label="Mobile Number" value={form.primary_phone} onChange={set("primary_phone")} />
                  </Grid>

                  <Grid item {...twoCol}>
                    <TextField fullWidth label="Address" value={form.address} onChange={set("address")} />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* STEP 1 */}
            {activeStep === 1 && (
              <Box>
                <SectionTitle>Emergency Contact</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item {...twoCol}>
                    <TextField fullWidth label="Emergency Contact Name" value={form.emergency_contact_name} onChange={set("emergency_contact_name")} />
                  </Grid>
                  <Grid item {...twoCol}>
                    <TextField fullWidth label="Relationship" value={form.emergency_relationship} onChange={set("emergency_relationship")} />
                  </Grid>
                  <Grid item {...twoCol}>
                    <TextField fullWidth label="Emergency Contact Number" value={form.emergency_contact_number} onChange={set("emergency_contact_number")} />
                  </Grid>
                  <Grid item {...twoCol}>
                    <TextField disabled fullWidth label="UHID" value={form.uhid || "---"} />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* STEP 2 */}
            {activeStep === 2 && (
              <Box>
                <SectionTitle>Admission Details</SectionTitle>

                <Grid container spacing={2}>
                  {/* Hospital dropdown */}
                  <Grid item {...twoCol}>
                    <TextField
                      select
                      fullWidth
                      label="Hospital / Organization *"
                      value={form.organization}
                      onChange={onOrgChange}
                      disabled={loadingOrg || loading}
                    >
                      <MenuItem value="">
                        {loadingOrg ? "Loading..." : "Select Hospital"}
                      </MenuItem>
                      {orgs.map((h) => (
                        <MenuItem key={pickHospitalId(h)} value={pickHospitalId(h)}>
                          {pickHospitalName(h)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item {...twoCol}>
                    <TextField disabled fullWidth label="Admission No" value="AUTO (after save)" />
                  </Grid>

                  {/* Date + time */}
                  <Grid item {...twoCol}>
                    <TextField
                      type="date"
                      fullWidth
                      label="Date of Admission *"
                      value={form.admission_date}
                      onChange={set("admission_date")}
                      InputLabelProps={{ shrink: true }}
                      disabled={!form.organization || loading}
                    />
                  </Grid>

                  <Grid item {...twoCol}>
                    <TextField
                      type="time"
                      fullWidth
                      label="Time of Admission *"
                      value={form.admission_time}
                      onChange={set("admission_time")}
                      InputLabelProps={{ shrink: true }}
                      disabled={!form.organization || loading}
                    />
                  </Grid>

                  {/* Department dropdown (optional) */}
                  <Grid item {...twoCol}>
                    <TextField
                      select
                      fullWidth
                      label="Department"
                      value={form.department}
                      onChange={set("department")}
                      disabled={!form.organization || loadingDept || loading}
                    >
                      <MenuItem value="">
                        {!form.organization ? "---" : loadingDept ? "Loading..." : "Select Department"}
                      </MenuItem>
                      {departments.map((d) => (
                        <MenuItem key={pickDeptId(d)} value={pickDeptName(d)}>
                          {pickDeptName(d)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* ✅ Ward ONLY dropdown */}
                  <Grid item {...twoCol}>
                    <TextField
                      select
                      fullWidth
                      label="Ward / Unit"
                      value={form.ward_unit_id || ""}
                      onChange={(e) => {
                        const wardId = e.target.value;
                        const wardObj = wards.find((w) => String(pickWardId(w)) === String(wardId));
                        setForm((p) => ({
                          ...p,
                          ward_unit_id: wardId,
                          ward_unit: wardObj ? pickWardName(wardObj) : "",
                        }));
                      }}
                      disabled={!form.organization || loadingWard || loading}
                    >
                      <MenuItem value="">
                        {!form.organization ? "---" : loadingWard ? "Loading..." : "Select Ward / Unit"}
                      </MenuItem>
                      {wards.map((w) => (
                        <MenuItem key={pickWardId(w)} value={pickWardId(w)}>
                          {pickWardName(w)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* ✅ Bed manual */}
                  <Grid item {...twoCol}>
                    <TextField
                      fullWidth
                      label="Bed Number (Manual)"
                      value={form.bed_number}
                      onChange={set("bed_number")}
                      disabled={!form.organization || loading}
                      placeholder="e.g. 12"
                    />
                  </Grid>

                  {/* ✅ Room manual */}
                  <Grid item {...twoCol}>
                    <TextField
                      fullWidth
                      label="Room Number (Manual)"
                      value={form.room_number}
                      onChange={set("room_number")}
                      disabled={!form.organization || loading}
                      placeholder="e.g. 101 / R-12"
                    />
                  </Grid>

                  <Grid item {...twoCol}>
                    <TextField
                      fullWidth
                      label="Treating Doctor"
                      value={form.treating_doctor}
                      onChange={set("treating_doctor")}
                      disabled={!form.organization || loading}
                    />
                  </Grid>

                  <Grid item {...twoCol}>
                    <TextField
                      fullWidth
                      label="Consultant"
                      value={form.consultant}
                      onChange={set("consultant")}
                      disabled={!form.organization || loading}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* STEP 3 */}
            {activeStep === 3 && (
              <Box>
                <SectionTitle>Review</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item {...twoCol}>
                    <TextField fullWidth label="Full Name" value={form.full_name} disabled />
                  </Grid>
                  <Grid item {...twoCol}>
                    <TextField fullWidth label="UHID" value={form.uhid} disabled />
                  </Grid>

                  <Grid item {...twoCol}>
                    <TextField fullWidth label="Department" value={form.department || "---"} disabled />
                  </Grid>
                  <Grid item {...twoCol}>
                    <TextField fullWidth label="Ward / Unit" value={form.ward_unit || "---"} disabled />
                  </Grid>

                  <Grid item {...twoCol}>
                    <TextField fullWidth label="Bed Number" value={form.bed_number || "---"} disabled />
                  </Grid>
                  <Grid item {...twoCol}>
                    <TextField fullWidth label="Room Number" value={form.room_number || "---"} disabled />
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 2 }}>
                  Admission No is generated automatically after Save.
                </Alert>
              </Box>
            )}

            {/* Buttons */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, mt: 1 }}>
              <Button variant="outlined" onClick={back} disabled={activeStep === 0 || loading}>
                Back
              </Button>

              {activeStep < 3 && (
                <Button variant="contained" onClick={next} disabled={loading}>
                  Next
                </Button>
              )}

              {activeStep === 2 && (
                <Button variant="contained" onClick={saveAll} disabled={loading}>
                  {loading ? "Saving..." : "Save Patient + Admission"}
                </Button>
              )}

              {activeStep === 3 && (
                <Button variant="outlined" onClick={resetAll} disabled={loading}>
                  New Entry
                </Button>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}